<template>
  <v-card outlined class="reorder-card">
    <v-card-text class="pa-4">
      <div class="reorder-header">
        <div>
          <div class="section-title">Reorder Point</div>
          <div class="section-subtitle">
            {{ Products.length }} product{{ Products.length === 1 ? "" : "s" }} need attention
          </div>
        </div>

        <v-btn small rounded outlined @click="openReport">
          <v-icon left small>mdi-file-chart-outline</v-icon>
          View Report
        </v-btn>
      </div>

      <v-skeleton-loader
        v-if="isLoading"
        type="list-item-two-line, list-item-two-line, list-item-two-line"
        class="mt-4"
      />

      <div v-else-if="Products.length > 0" class="product-list mt-4">
        <div
          v-for="product in Products"
          :key="product.id"
          class="product-row"
        >
          <div class="product-main">
            <div class="product-icon">
              <v-icon color="orange darken-2">mdi-package-variant</v-icon>
            </div>

            <div class="product-info">
              <div class="product-title">
                {{ product.name }}

                <v-chip
                  small
                  class="ml-1 urgency-chip"
                  :color="getUrgencyColor(product)"
                  :text-color="getUrgencyTextColor(product)"
                >
                  {{ getUrgencyLabel(product) }}
                </v-chip>
              </div>

              <div class="product-meta">
                Current: {{ getBalance(product) }}
                <span class="dot">•</span>
                Min: {{ getMin(product) }}
                <span class="dot">•</span>
                Max: {{ getMax(product) }}
              </div>

              <v-progress-linear
                class="mt-2 stock-progress"
                rounded
                height="7"
                :value="getStockPercent(product)"
                :color="getProgressColor(product)"
                background-color="#e2e8f0"
              />
            </div>
          </div>

          <div class="reorder-side">
            <div class="reorder-label">Reorder</div>
            <div class="reorder-qty">
              {{ getReorderQty(product) }}
            </div>
          </div>
        </div>
      </div>

      <div v-else class="empty-state">
        <lottie
          :height="160"
          :width="160"
          :options="defaultOptions"
          v-on:animCreated="handleAnimation"
        />

        <div class="empty-title">No reorder alerts</div>
        <div class="empty-subtitle">Inventory levels look good.</div>
      </div>
    </v-card-text>
  </v-card>
</template>

<script>
import productService from "@/modules/Product/service.js";
import Lottie from "@/components/lottie.vue";
import * as animationData from "@/assets/lottie/empty_box.json";

export default {
  name: "ReOrderList",

  components: {
    Lottie,
  },

  data() {
    return {
      Products: [],
      isLoading: true,
      defaultOptions: {
        loop: true,
        autoplay: true,
        animationData: animationData.default,
      },
    };
  },

  mounted() {
    this.getAll();
  },

  methods: {
    handleAnimation(anim) {
      this.anim = anim;
    },

    openReport() {
      const routeData = this.$router.resolve({
        path: "/reportView/product/reorder",
      });

      window.open(routeData.href, "_blank");
    },

    getBalance(product) {
      return Number(product.balance || 0);
    },

    getMin(product) {
      return Number(product.min || 0);
    },

    getMax(product) {
      return Number(product.max || 0);
    },

    getReorderQty(product) {
      const reorder = this.getMax(product) - this.getBalance(product);
      return reorder > 0 ? reorder : 0;
    },

    getStockPercent(product) {
      const max = this.getMax(product);
      const balance = this.getBalance(product);

      if (!max || max <= 0) {
        return 0;
      }

      const percent = (balance / max) * 100;

      if (percent > 100) {
        return 100;
      }

      if (percent < 0) {
        return 0;
      }

      return percent;
    },

    getUrgencyLabel(product) {
      const balance = this.getBalance(product);
      const min = this.getMin(product);

      if (balance <= 0) {
        return "Out";
      }

      if (balance <= min) {
        return "Critical";
      }

      return "Low";
    },

    getUrgencyColor(product) {
      const label = this.getUrgencyLabel(product);

      if (label === "Out") {
        return "red lighten-5";
      }

      if (label === "Critical") {
        return "orange lighten-5";
      }

      return "blue lighten-5";
    },

    getUrgencyTextColor(product) {
      const label = this.getUrgencyLabel(product);

      if (label === "Out") {
        return "red darken-3";
      }

      if (label === "Critical") {
        return "orange darken-3";
      }

      return "blue darken-3";
    },

    getProgressColor(product) {
      const label = this.getUrgencyLabel(product);

      if (label === "Out") {
        return "red";
      }

      if (label === "Critical") {
        return "orange";
      }

      return "blue";
    },

    async getAll() {
      this.isLoading = true;

      try {
        const result = await productService.minimumReached();
        this.Products = result.data || [];
      } catch (error) {
        console.error("minimumReached failed", error);
        this.Products = [];
      } finally {
        this.isLoading = false;
      }
    },
  },
};
</script>

<style scoped>
.reorder-card {
  border-radius: 20px;
  background: #ffffff;
}

.reorder-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
  flex-wrap: wrap;
}

.section-title {
  font-size: 18px;
  font-weight: 700;
  color: #0f172a;
}

.section-subtitle {
  font-size: 13px;
  color: #64748b;
  margin-top: 2px;
}

.product-list {
  max-height: 500px;
  overflow-y: auto;
  padding-right: 4px;
}

.product-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
  border: 1px solid #e2e8f0;
  border-radius: 16px;
  padding: 14px;
  margin-bottom: 10px;
  transition: all 0.2s ease;
}

.product-row:hover {
  background: #f8fafc;
  border-color: #cbd5e1;
}

.product-main {
  display: flex;
  align-items: center;
  min-width: 0;
  gap: 12px;
  flex: 1;
}

.product-icon {
  width: 42px;
  height: 42px;
  min-width: 42px;
  border-radius: 14px;
  background: #fff7ed;
  display: flex;
  align-items: center;
  justify-content: center;
}

.product-info {
  min-width: 0;
  flex: 1;
}

.product-title {
  font-size: 14px;
  font-weight: 700;
  color: #0f172a;
  display: flex;
  align-items: center;
  flex-wrap: wrap;
}

.product-meta {
  font-size: 12px;
  color: #64748b;
  margin-top: 4px;
}

.stock-progress {
  max-width: 260px;
}

.dot {
  margin: 0 6px;
  color: #94a3b8;
}

.urgency-chip {
  font-weight: 700;
}

.reorder-side {
  text-align: right;
  min-width: 70px;
}

.reorder-label {
  font-size: 11px;
  color: #64748b;
}

.reorder-qty {
  font-size: 22px;
  font-weight: 800;
  color: #0f172a;
  line-height: 1.1;
}

.empty-state {
  text-align: center;
  padding: 24px 0;
}

.empty-title {
  font-size: 15px;
  font-weight: 700;
  color: #0f172a;
}

.empty-subtitle {
  font-size: 13px;
  color: #64748b;
  margin-top: 4px;
}

@media (max-width: 600px) {
  .product-row {
    align-items: flex-start;
  }

  .reorder-side {
    min-width: 50px;
  }

  .stock-progress {
    max-width: 100%;
  }
}
</style>